import { useEffect, lazy, Suspense } from "react";
import HeroSection from "@/components/sections/HeroSection";
import SEO, { BOAT_VALUATION_KEYWORDS } from "@/components/SEO";
import SpeakableContent from "@/components/geo/SpeakableContent";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Bot, Clock, Zap, CheckCircle, BarChart3, CreditCard, Shield, FileText, Users, ArrowRight } from "lucide-react";
import { trackCTAClick, trackScrollDepth, trackNavigation } from "@/utils/analytics";

// scroll-reveal is now CSS-only (no framer-motion) — import directly instead of lazy
import { ScrollReveal, StaggerContainer, StaggerItem, CounterReveal } from "@/components/ui/scroll-reveal";

// Lazy load below-fold section components to reduce initial JS bundle (~200KB savings)
const QuickAnswersSection = lazy(() => import("@/components/sections/QuickAnswersSection"));
const HowItWorksSection = lazy(() => import("@/components/sections/HowItWorksSection"));
const WhyChooseUsSection = lazy(() => import("@/components/sections/WhyChooseUsSection"));
const ReportPreviewSection = lazy(() => import("@/components/sections/ReportPreviewSection"));
const ResourcesSection = lazy(() => import("@/components/sections/ResourcesSection"));

// Lazy load GEO optimization components (data tables, charts)
const DepreciationDataTable = lazy(() => import("@/components/geo/DepreciationDataTable"));
const EnhancedFAQ = lazy(() => import("@/components/geo/EnhancedFAQ"));
const ComparisonTable = lazy(() => import("@/components/geo/ComparisonTable"));
const ContentFreshness = lazy(() => import("@/components/geo/ContentFreshness"));
const SourceCitation = lazy(() => import("@/components/geo/SourceCitation"));
const ItemListSchema = lazy(() => import("@/components/geo/ItemListSchema"));

// Lazy load CTA components
const NextStepsModule = lazy(() => import("@/components/cta/NextStepsModule").then(m => ({ default: m.NextStepsModule })));
const BuyerCTASection = lazy(() => import("@/components/sections/BuyerCTASection"));
const UpgradePromoBanner = lazy(() => import("@/components/subscription/UpgradePromoBanner").then(m => ({ default: m.UpgradePromoBanner })));

// Minimal loading placeholder for below-fold sections
const SectionLoader = ({ height = 200 }: { height?: number }) => (
  <div 
    className="bg-muted/10" 
    style={{ minHeight: `${height}px` }}
  />
);

export default function HomePage() {
  // Track scroll depth - deferred to avoid forced reflow on initial render
  useEffect(() => {
    let maxScrollDepth = 0;
    let rafId: number | null = null;
    
    const handleScroll = () => {
      // Batch DOM reads using requestAnimationFrame to avoid forced reflow
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const scrollPercent = Math.round(
          (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
        );
        
        if (scrollPercent > maxScrollDepth && scrollPercent >= 25) {
          maxScrollDepth = scrollPercent;
          // Track at 25%, 50%, 75%, 100%
          if ([25, 50, 75, 100].includes(scrollPercent)) {
            trackScrollDepth(scrollPercent, 'home');
          }
        }
        rafId = null;
      });
    };

    // Defer scroll listener attachment to avoid initial forced reflow
    const deferredSetup = requestAnimationFrame(() => {
      window.addEventListener('scroll', handleScroll, { passive: true });
    });
    
    return () => {
      cancelAnimationFrame(deferredSetup);
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <SEO 
        title="Instant Yacht Valuation & Market Intelligence | YachtValues™"
        description="Private yacht valuation and market intelligence for luxury vessels 40ft+. Data-driven insights for discerning owners, buyers, and advisors. Instant. No pressure."
        keywords="yacht valuation, yacht value, confidential yacht valuation, yacht market intelligence, yacht appraisal, luxury yacht value, motor yacht value, what is my yacht worth, yacht depreciation, yacht market analysis"
        canonicalUrl="https://yachtvalues.com/"
        ogImage="https://yachtvalues.com/og-image-boat-valuation.jpg"
      />
      <div className="min-h-screen" style={{ contain: 'layout' }}>
        <HeroSection />
        
        {/* Quick Answers Section - Wrapped with SpeakableContent for voice/AI search */}
        <SpeakableContent
          pageUrl="https://yachtvalues.com/"
          headline="Instant Yacht Valuation | YachtValues™"
          cssSelector=".quick-answer"
        >
          <Suspense fallback={<SectionLoader height={500} />}>
            <QuickAnswersSection />
          </Suspense>
        </SpeakableContent>

        {/* ContentFreshness for dateModified schema signal */}
        <Suspense fallback={null}>
          <ContentFreshness
            lastUpdated="2026-02-23"
            publishedDate="2025-06-01"
            updateFrequency="Daily"
            className="container mx-auto px-4 py-2"
          />
        </Suspense>
        
        {/* Trust section - CSS-only animation (no Framer Motion) for LCP optimization */}
        <section 
          className="py-6 md:py-8 bg-muted/10 border-y border-border"
        >
          <div className="container mx-auto px-2 sm:px-4">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-base md:text-lg text-muted-foreground mb-4 md:mb-6">
                Trusted by yacht owners, brokers, and marine professionals
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 text-xs sm:text-sm text-muted-foreground">
                <div>✓ Instant Valuations</div>
                <div>✓ Data-Driven Insights</div>
                <div>✓ Luxury Vessels 40ft+</div>
                <div>✓ No Pressure. No Obligation.</div>
              </div>
            </div>
          </div>
        </section>

        {/* Buyer CTA Section - Mid-page with AI-generated imagery */}
        <div>
          <Suspense fallback={<SectionLoader height={450} />}>
            <BuyerCTASection />
          </Suspense>
        </div>

      {/* How It Works with HowTo Schema - no contentVisibility to ensure schema is discoverable */}
      <div>
        <Suspense fallback={<SectionLoader height={600} />}>
          <HowItWorksSection />
        </Suspense>
      </div>

      <div>
        <Suspense fallback={<SectionLoader height={500} />}>
          <WhyChooseUsSection />
        </Suspense>
      </div>

      {/* Partner CTAs - Next Steps Module */}
      <div>
        <Suspense fallback={<SectionLoader height={400} />}>
          <NextStepsModule
            context="homepage_below_fold"
            title="Ready to Take the Next Step?"
            subtitle="Whether you're buying, selling, or just exploring, these resources help you make confident decisions."
          />
        </Suspense>
      </div>

      {/* Report Preview Section */}
      <div>
        <Suspense fallback={<SectionLoader height={600} />}>
          <ReportPreviewSection />
        </Suspense>
      </div>

        {/* GEO Optimization: Comparison Table - height reserved to prevent CLS */}
        <div>
          <Suspense fallback={<SectionLoader height={450} />}>
            <ScrollReveal variant="fadeUp" delay={0.1}>
              <section className="py-8 md:py-12 bg-muted/10">
                <div className="container mx-auto px-2 sm:px-4">
                  <div className="max-w-5xl mx-auto">
                    <Suspense fallback={<SectionLoader height={350} />}>
                      <ComparisonTable
                        title="YachtValues™ vs Other Valuation Services"
                        description="See how our instant yacht valuation compares to traditional services"
                        items={["YachtValues™", "NADA", "KBB", "ABOS"]}
                        comparisons={[
                          { feature: "Instant Access", values: [true, "Basic only", "Limited", false] },
                          { feature: "AI-Powered", values: [true, false, false, false] },
                          { feature: "Real-Time Market Data", values: [true, "Partial", "Limited", "Partial"] },
                          { feature: "Discreet Sale Options", values: [true, false, false, false] },
                          { feature: "No Registration Required", values: [true, false, "Limited", false] },
                          { feature: "Private Intelligence Report", values: [true, "Pro only", "Limited", true] },
                          { feature: "Regional Market Analysis", values: [true, "Limited", "Limited", "Partial"] },
                          { feature: "Update Frequency", values: ["Daily", "Quarterly", "Annually", "Quarterly"] }
                        ]}
                      />
                    </Suspense>
                  </div>
                </div>
              </section>
            </ScrollReveal>
          </Suspense>
        </div>

        {/* GEO Optimization: Depreciation Data Table - height reserved to prevent CLS */}
        <div>
          <Suspense fallback={<SectionLoader height={400} />}>
            <ScrollReveal variant="fadeUp" delay={0.1}>
              <section className="py-8 md:py-12 bg-background">
                <div className="container mx-auto px-2 sm:px-4">
                  <div className="max-w-4xl mx-auto">
                    <Suspense fallback={<SectionLoader height={300} />}>
                      <DepreciationDataTable />
                    </Suspense>
                    <div className="mt-6">
                      <Suspense fallback={null}>
                        <SourceCitation
                          sources={[
                            { name: "National Marine Manufacturers Association (NMMA)", description: "Annual recreational boating statistics", url: "https://www.nmma.org" },
                            { name: "NADA Marine Appraisal Guide", description: "Industry standard boat pricing guide" },
                            { name: "J.D. Power Marine Values", description: "Trusted marine valuation data" },
                            { name: "YachtValues Market Data", description: "Proprietary AI-powered sales analysis", accessDate: "2026-02-19" }
                          ]}
                        />
                      </Suspense>
                    </div>
                  </div>
                </div>
              </section>
            </ScrollReveal>
          </Suspense>
        </div>

        {/* ItemList Schema for Yacht Types */}
        <Suspense fallback={null}>
          <ItemListSchema
            listName="Yacht Types for Valuation"
            items={[
              { name: "Motor Yacht Values", url: "https://yachtvalues.com/boat-value/motor-yachts", description: "Luxury motor yachts & cruisers" },
              { name: "Sport Yacht Values", url: "https://yachtvalues.com/boat-value/sport-yachts", description: "Performance sport yachts" },
              { name: "Flybridge Yacht Values", url: "https://yachtvalues.com/boat-value/flybridge-yachts", description: "Flybridge cruisers & motor yachts" },
              { name: "Express Cruiser Values", url: "https://yachtvalues.com/boat-value/express-cruisers", description: "Express cruiser yachts" },
              { name: "Trawler Yacht Values", url: "https://yachtvalues.com/boat-value/trawler-yachts", description: "Long-range trawler yachts" },
              { name: "Catamaran Values", url: "https://yachtvalues.com/boat-value/catamarans", description: "Power & sailing catamarans" },
              { name: "Sailboat Values", url: "https://yachtvalues.com/boat-value/sailboats", description: "Cruising & performance sailboats" },
              { name: "Center Console Values", url: "https://yachtvalues.com/boat-value/center-console-boats", description: "Offshore fishing & cruising" },
            ]}
          />
        </Suspense>

        {/* Yacht Type Values Section - height reserved to prevent CLS */}
        <div>
          <Suspense fallback={<SectionLoader height={1200} />}>
            <ScrollReveal variant="fadeUp">
              <section className="py-10 md:py-24 bg-background">
                <div className="container mx-auto px-2 sm:px-4">
                  <div className="max-w-6xl mx-auto">
                    <ScrollReveal variant="fadeUp">
                      <div className="text-center mb-8 md:mb-12">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
                          Accurate Values for Every Yacht Type
                        </h2>
                        <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto mb-6 md:mb-8 px-2">
                          Whether you're assessing market position, planning an acquisition, or evaluating your portfolio, 
                          our AI-powered system provides data-driven valuations with type-specific market insights.
                        </p>
                      </div>
                    </ScrollReveal>
                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" staggerDelay={0.06}>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link 
                          to="/boat-value/motor-yachts" 
                          className="text-primary hover:underline"
                          onClick={() => trackNavigation('Motor Yacht Values', '/boat-value/motor-yachts', 'boat_types_section')}
                        >
                          Motor Yacht Values
                        </Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Luxury motor yachts & cruisers</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/sport-yachts" className="text-primary hover:underline">Sport Yacht Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Performance sport yachts</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/flybridge-yachts" className="text-primary hover:underline">Flybridge Yacht Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Flybridge cruisers & motor yachts</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/express-cruisers" className="text-primary hover:underline">Express Cruiser Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Express cruiser yachts</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/trawler-yachts" className="text-primary hover:underline">Trawler Yacht Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Long-range trawler yachts</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/catamarans" className="text-primary hover:underline">Catamaran Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Power & sailing catamarans</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/sailboats" className="text-primary hover:underline">Sailboat Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Cruising & performance sailboats</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/center-console-boats" className="text-primary hover:underline">Center Console Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Offshore fishing & cruising</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/cabin-cruisers" className="text-primary hover:underline">Cabin Cruiser Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Overnight cruising vessels</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/powerboats" className="text-primary hover:underline">Powerboat Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Performance powerboats</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/yachts" className="text-primary hover:underline">Yacht Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">All yacht categories</p>
                    </Card>
                  </StaggerItem>
                  <StaggerItem>
                    <Card className="p-4 md:p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                      <h3 className="text-lg md:text-xl font-semibold mb-2">
                        <Link to="/boat-value/houseboats" className="text-primary hover:underline">Houseboat Values</Link>
                      </h3>
                      <p className="text-sm text-muted-foreground">Residential & cruising houseboats</p>
                    </Card>
                  </StaggerItem>
                    </StaggerContainer>
                    
                    {/* More Yacht Types Link */}
                    <div className="text-center mt-8">
                      <p className="text-muted-foreground mb-4">
                        We also value{" "}
                        <Link to="/boat-value/offshore-boats" className="text-primary hover:underline">offshore vessels</Link>, 
                        {" "}<Link to="/boat-value/bay-boats" className="text-primary hover:underline">bay boats</Link>, 
                        {" "}<Link to="/boat-value/cruiser-boats" className="text-primary hover:underline">cruisers</Link>, 
                        {" "}<Link to="/boat-value/bowrider-boats" className="text-primary hover:underline">bowriders</Link>, 
                        {" "}and more.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </ScrollReveal>
          </Suspense>
        </div>

        {/* Regional Boat Values Section */}
        <div>
        <ScrollReveal variant="fadeUp">
          <section className="py-10 md:py-24 bg-muted/30">
            <div className="container mx-auto px-2 sm:px-4">
              <div className="max-w-6xl mx-auto">
                <ScrollReveal variant="fadeUp">
                  <div className="text-center mb-8 md:mb-12">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
                      Yacht Values by Region
                    </h2>
                    <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto px-2">
                      Accurate yacht valuations tailored to your local market. Our AI analyzes regional pricing trends, 
                      seasonal demand, and location-specific factors for the most precise assessments.
                    </p>
                  </div>
                </ScrollReveal>

                <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" staggerDelay={0.06}>
                  {[
                    { path: "/boat-value/california", name: "California Yacht Values", desc: "San Diego to San Francisco — coastal and inland markets" },
                    { path: "/boat-value/florida", name: "Florida Yacht Values", desc: "Miami to Panhandle — saltwater and freshwater markets" },
                    { path: "/boat-value/texas", name: "Texas Yacht Values", desc: "Gulf Coast to Highland Lakes — diverse marine markets" },
                    { path: "/boat-value/washington", name: "Washington Yacht Values", desc: "Puget Sound to Columbia River valuations" },
                    { path: "/boat-value/new-york", name: "New York Yacht Values", desc: "Long Island to Finger Lakes market analysis" },
                    { path: "/boat-value/virginia", name: "Virginia Yacht Values", desc: "Chesapeake Bay to Smith Mountain Lake" },
                    { path: "/boat-value/north-carolina", name: "North Carolina Yacht Values", desc: "Outer Banks to mountain lakes valuations" },
                    { path: "/boat-value/south-carolina", name: "South Carolina Yacht Values", desc: "Charleston to Lake Murray pricing trends" },
                    { path: "/boat-value/lake-ozarks", name: "Lake Ozarks Yacht Values", desc: "Premier Midwest marine destination values" },
                    { path: "/boat-value/arizona", name: "Arizona Yacht Values", desc: "Lake Powell to Lake Havasu market pricing" },
                    { path: "/boat-value/colorado", name: "Colorado Yacht Values", desc: "Mountain lake and reservoir valuations" },
                    { path: "/boat-value/new-jersey", name: "New Jersey Yacht Values", desc: "Jersey Shore to inland lake pricing" },
                    { path: "/boat-value/oregon", name: "Oregon Yacht Values", desc: "Pacific Coast to Crater Lake valuations" },
                  ].map((region) => (
                    <StaggerItem key={region.path}>
                      <Card className="p-6 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300">
                        <h3 className="text-xl font-semibold mb-2">
                          <Link to={region.path} className="text-primary hover:underline">
                            {region.name}
                          </Link>
                        </h3>
                        <p className="text-sm text-muted-foreground">{region.desc}</p>
                      </Card>
                    </StaggerItem>
                  ))}
                </StaggerContainer>

                <ScrollReveal variant="fadeUp" delay={0.2}>
                  <div className="text-center">
                    <p className="text-muted-foreground mb-6">
                      Don't see your region? Our AI-powered valuation tool covers all 50 states and analyzes 
                      local market conditions wherever your yacht is located.
                    </p>
                    <Button 
                      asChild 
                      size="lg" 
                      variant="outline"
                      onClick={() => trackCTAClick('regional_yacht_value', '/valuation', 'regional_section')}
                    >
                      <Link to="/valuation">Get Your Regional Yacht Value</Link>
                    </Button>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </section>
        </ScrollReveal>
        </div>
        
        {/* GEO Optimization: Enhanced FAQ Section - Single consolidated FAQPage schema */}
        <div>
        <ScrollReveal variant="fadeUp">
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                {/* Single consolidated FAQPage schema including QuickAnswers FAQs */}
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [
                      // Quick Answers FAQs
                      {
                        "@type": "Question",
                        "name": "What's My Yacht Worth?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Your yacht's market position depends on year, make, model, length, condition, and location. Our AI-powered system analyzes current market data and comparable activity to provide an informational valuation estimate."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "How Do I Value My Yacht?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Valuing your yacht involves gathering vessel details (year, make, model, length, condition), analyzing comparable market activity, and understanding regional pricing trends. Our system combines these methods to deliver a data-driven estimate."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "What Affects Yacht Values?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Yacht values are affected by age, condition, engine hours, builder reputation, length, location, seasonal demand, maintenance history, and market trends. Premium builders like Azimut, Sunseeker, and Viking hold value better, while documented maintenance and low engine hours can increase your yacht's worth by 15-25%."
                        }
                      },
                      // Main FAQ section
                      {
                        "@type": "Question",
                        "name": "How accurate are online yacht valuations?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Our AI-powered yacht valuations analyze over 1 million data points including recent comparable sales, current market listings, regional demand, and depreciation patterns. Results are informational estimates based on current market data, not certified appraisals."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "What's the difference between NADA boat values and YachtValues.com?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "NADA requires paid subscriptions and updates quarterly, while YachtValues.com updates daily with real-time market data. Our AI-powered system also provides private market intelligence summaries and discreet sale options."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "How much do boats depreciate per year?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Boats typically depreciate 8-10% in the first year, then 6-8% annually for years 2-5, and 4-6% for years 6-10. Depreciation slows to 3-5% after 10 years. Actual rates vary by boat type, brand, condition, and maintenance history."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Is yacht valuation free on YachtValues.com?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Yes, our complimentary valuation snapshot is free. YachtValues.com provides AI-powered market estimates with no registration required. Premium intelligence reports and concierge services are available for those seeking deeper insights."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "How long does a yacht valuation take?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Our yacht valuation takes under 2 minutes. Simply enter your vessel details (year, make, model, condition), and receive comprehensive low, medium, and high value estimates with detailed market analysis."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Can I explore discreet sale options for my yacht?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Yes. After receiving your valuation, you can explore discreet sale options through our concierge service. We may connect you with a verified acquisition partner aligned with your vessel and timeline."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "What information do I need to value my yacht?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "For basic valuation, you need: year, make, model, and length. For more accurate results, provide: HIN number, engine type and hours, condition assessment, current location, and any upgrades or modifications."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Are yacht values higher in certain states or regions?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Yes, yacht values vary significantly by region. Florida, California, and coastal states typically have higher values due to year-round boating seasons and higher demand. Inland states may see 10-20% lower values."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "When is the best time to sell my yacht?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Spring (March-May) is typically the best time to sell yachts, when demand peaks as boating season begins. You can expect 10-15% higher prices in spring versus fall/winter."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "How do I increase my yacht's resale value?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Maximize resale value by: keeping detailed maintenance records (adds 10-15% value), addressing all mechanical issues before listing, professional detailing, updating electronics and safety equipment, and timing your sale for spring market peak."
                        }
                      }
                    ]
                  })}
                </script>
                
                <Suspense fallback={<SectionLoader height={800} />}>
                  <EnhancedFAQ
                    title="Yacht Valuation: Frequently Asked Questions"
                    description="Get answers to the most common questions about yacht values and valuations"
                    includeSchema={false}
                    faqs={[
                      {
                        question: "How accurate are online yacht valuations?",
                        answer: "Our AI-powered yacht valuations analyze over 1 million data points including recent comparable sales, current market listings, regional demand, and depreciation patterns. Results are informational estimates based on current market data, not certified appraisals. Accuracy depends on providing complete vessel information and current market conditions."
                      },
                      {
                        question: "What's the difference between NADA boat values and YachtValues.com?",
                        answer: "NADA requires paid subscriptions and updates quarterly, while YachtValues.com updates daily with real-time market data. Our AI-powered system also provides private market intelligence summaries and discreet sale options."
                      },
                      {
                        question: "How much do yachts depreciate per year?",
                        answer: "Yachts typically depreciate 8-10% in the first year, then 6-8% annually for years 2-5, and 4-6% for years 6-10. Depreciation slows to 3-5% after 10 years. Actual rates vary by vessel type, builder, condition, and maintenance history."
                      },
                      {
                        question: "Is yacht valuation free on YachtValues.com?",
                        answer: "Yes, our complimentary valuation snapshot is free. YachtValues.com provides AI-powered market estimates with no registration required. Premium intelligence reports and concierge services are available for those seeking deeper insights."
                      },
                      {
                        question: "How long does a yacht valuation take?",
                        answer: "Our yacht valuation takes under 2 minutes. Simply enter your vessel details (year, make, model, condition), and receive comprehensive low, medium, and high value estimates with detailed market analysis. Download your PDF report instantly."
                      },
                      {
                        question: "Can I explore discreet sale options for my yacht?",
                        answer: "Yes. After receiving your valuation, you can explore discreet sale options through our concierge service. We may connect you with a verified acquisition partner aligned with your vessel and timeline."
                      },
                      {
                        question: "What information do I need to value my yacht?",
                        answer: "For basic valuation, you need: year, make, model, and length. For more accurate results, provide: HIN number, engine type and hours, condition assessment, current location, and any upgrades or modifications. More details = more precise valuation."
                      },
                      {
                        question: "Are yacht values higher in certain states or regions?",
                        answer: "Yes, yacht values vary significantly by region. Florida, California, and coastal states typically have higher values due to year-round boating seasons and higher demand. Inland states may see 10-20% lower values, while premium locations like Florida Keys can see 15-25% higher values."
                      },
                      {
                        question: "When is the best time to sell my yacht?",
                        answer: "Spring (March-May) is typically the best time to sell yachts, when demand peaks as boating season begins. You can expect 10-15% higher prices in spring versus fall/winter. However, some regions like Florida have strong demand year-round."
                      },
                      {
                        question: "How do I increase my yacht's resale value?",
                        answer: "Improve resale positioning by: keeping detailed maintenance records, addressing all mechanical issues before listing, professional detailing, updating electronics and safety equipment, providing survey reports, and timing your sale for peak seasonal demand."
                      },
                      {
                        question: "Do yacht values include trailer value?",
                        answer: "Standard yacht valuations typically do NOT include trailer value unless specifically noted. Trailers add $1,000-$8,000 depending on size and condition. Always clarify whether the quoted value includes or excludes the trailer when comparing offers or listings."
                      },
                      {
                        question: "What determines yacht values in 2026?",
                        answer: "2026 yacht values are determined by supply/demand balance, interest rates, new vessel pricing, regional demand, seasonal timing, fuel costs, and economic conditions. Current trends show stabilizing prices after the post-pandemic surge, with premium yachts maintaining strong values."
                      }
                    ]}
                  />
                </Suspense>
              </div>
            </div>
          </section>
        </ScrollReveal>
        </div>
        
        {/* Superyacht Market Intelligence Section */}
        <div>
          <Suspense fallback={<SectionLoader height={600} />}>
            <ScrollReveal variant="fadeUp">
              <section className="py-12 md:py-20 bg-primary/5 border-y border-primary/10">
                <div className="container mx-auto px-4">
                  <div className="max-w-6xl mx-auto">
                    <ScrollReveal variant="fadeUp">
                      <div className="text-center mb-10">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3">
                          Superyacht Market Intelligence
                        </h2>
                        <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
                          Authoritative analysis across six research clusters — for owners, advisors, and family offices seeking a disciplined understanding of the superyacht market.
                        </p>
                      </div>
                    </ScrollReveal>
                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" staggerDelay={0.06}>
                      {[
                        { href: "/yacht-asset-class", title: "Yacht as an Asset Class", desc: "Investment strategy, depreciation economics, and wealth preservation frameworks." },
                        { href: "/superyacht-market-analysis", title: "Superyacht Market Analysis", desc: "Secondary market trends, demand drivers, and 2026 forecasts." },
                        { href: "/yacht-resale-liquidity", title: "Resale & Liquidity", desc: "The Yacht Liquidity Compression Model™ and exit-planning intelligence." },
                        { href: "/shipyard-backlog-pricing", title: "Shipyard Backlog Pricing", desc: "The Shipyard Scarcity Index™ and its impact on pre-owned valuations." },
                        { href: "/yacht-market-reports", title: "Market Reports by Size Tier", desc: "Dedicated intelligence for 40m+, 60m+, and 100m+ vessel segments." },
                        { href: "/intelligence", title: "Full Intelligence Center", desc: "29 in-depth articles across six research clusters. Expert analysis, no noise." },
                      ].map(item => (
                        <StaggerItem key={item.href}>
                          <Card className="p-5 h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                            <h3 className="text-base font-semibold mb-2">
                              <Link to={item.href} className="text-primary hover:underline">{item.title}</Link>
                            </h3>
                            <p className="text-sm text-muted-foreground">{item.desc}</p>
                          </Card>
                        </StaggerItem>
                      ))}
                    </StaggerContainer>
                  </div>
                </div>
              </section>
            </ScrollReveal>
          </Suspense>
        </div>

        {/* Heritage Builder Authority Section */}
        <div>
          <Suspense fallback={<SectionLoader height={500} />}>
            <ScrollReveal variant="fadeUp">
              <section className="py-12 md:py-20 bg-background">
                <div className="container mx-auto px-4">
                  <div className="max-w-6xl mx-auto">
                    <ScrollReveal variant="fadeUp">
                      <div className="text-center mb-10">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3">
                          Heritage Builder Valuations
                        </h2>
                        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                          Builder-specific valuation intelligence for Tier 1 superyacht yards — covering heritage, retention characteristics, and secondary market dynamics.
                        </p>
                      </div>
                    </ScrollReveal>
                    <StaggerContainer className="grid md:grid-cols-3 lg:grid-cols-5 gap-4" staggerDelay={0.05}>
                      {[
                        { href: "/yacht-builder/feadship", name: "Feadship", sub: "Netherlands · Custom" },
                        { href: "/yacht-builder/lurssen", name: "Lürssen", sub: "Germany · 100m+" },
                        { href: "/yacht-builder/amels", name: "Amels", sub: "Netherlands · Limited Editions" },
                        { href: "/yacht-builder/heesen", name: "Heesen", sub: "Netherlands · Fast Displacement" },
                        { href: "/yacht-builder/benetti", name: "Benetti", sub: "Italy · Volume Leader" },
                      ].map(b => (
                        <StaggerItem key={b.href}>
                          <Card className="p-4 text-center h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                            <h3 className="font-semibold mb-1">
                              <Link to={b.href} className="text-primary hover:underline">{b.name}</Link>
                            </h3>
                            <p className="text-xs text-muted-foreground">{b.sub}</p>
                          </Card>
                        </StaggerItem>
                      ))}
                    </StaggerContainer>
                    <div className="text-center mt-6">
                      <Button variant="outline" asChild>
                        <Link to="/yacht-builders">All Builder Intelligence <ArrowRight className="h-4 w-4 ml-1" /></Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            </ScrollReveal>
          </Suspense>
        </div>

        <div>
          <Suspense fallback={<SectionLoader height={500} />}>
            <ResourcesSection />
          </Suspense>
        </div>

        {/* Trust & Closing Section */}
        <div>
        <ScrollReveal variant="fadeUp">
          <section className="py-16 bg-muted/10 border-t border-border">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <ScrollReveal variant="fadeUp">
                  <h2 className="text-3xl md:text-4xl font-bold mb-8">
                    Trusted by Yacht Owners &amp; Marine Professionals
                  </h2>
                </ScrollReveal>
                
                <StaggerContainer className="grid md:grid-cols-3 gap-8 mb-12" staggerDelay={0.15}>
                  <StaggerItem variant="scale">
                    <div className="p-6">
                      <div className="text-3xl font-bold text-primary mb-2">
                        <CounterReveal value={50000} suffix="+" />
                      </div>
                      <p className="text-muted-foreground">Yacht Valuations Completed</p>
                    </div>
                  </StaggerItem>
                  <StaggerItem variant="scale">
                    <div className="p-6">
                      <div className="text-3xl font-bold text-primary mb-2">Daily</div>
                      <p className="text-muted-foreground">Market Data Updates</p>
                    </div>
                  </StaggerItem>
                  <StaggerItem variant="scale">
                    <div className="p-6">
                      <div className="text-3xl font-bold text-primary mb-2">24/7</div>
                      <p className="text-muted-foreground">Instant Online Access</p>
                    </div>
                  </StaggerItem>
                </StaggerContainer>
                
                <ScrollReveal variant="fadeUp" delay={0.2}>
                  <div className="bg-background rounded-lg p-4 sm:p-8 shadow-sm mb-8">
                    <div className="grid md:grid-cols-2 gap-6">
                       <div className="md:border-r md:pr-6">
                        <h3 className="text-xl font-semibold mb-3">
                          Confidential Yacht Valuation
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          Data-driven market estimates with no registration required. Understand your market position in minutes.
                        </p>
                        <Button asChild size="lg" className="w-full">
                          <Link to="/valuation">Start Confidential Valuation</Link>
                        </Button>
                      </div>
                      
                      <div className="md:pl-6">
                        <h3 className="text-xl font-semibold mb-3">
                          Market-Based Valuation Estimate
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          Informational valuation reports for insurance, financing, and market positioning. Based on current market data and comparable activity.
                        </p>
                        <Button asChild size="lg" variant="outline" className="w-full">
                          <Link to="/boat-appraisal">Learn More</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
                
                <ScrollReveal variant="fadeUp" delay={0.3}>
                  <div className="text-sm text-muted-foreground">
                    <p className="mb-2">
                      ⚓ <strong>Things Can Break — Let Us Know.</strong>
                    </p>
                    <p>
                      As a yacht owner, you know that no matter how diligently you maintain something, things can break. 
                      If you notice anything in your report that doesn't look quite right, please{" "}
                      <a 
                        href="/contact" 
                        className="text-primary hover:text-primary/80 underline transition-colors"
                      >
                        let us know
                      </a>{" "}
                      so we can review it and fix it.
                    </p>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </section>
        </ScrollReveal>
        </div>

        {/* Upgrade CTA - Before Footer */}
        <div>
          <Suspense fallback={<SectionLoader height={400} />}>
            <UpgradePromoBanner variant="hero" />
          </Suspense>
        </div>
      </div>
    </>
  );
}